/**
 * components/landing/platform/sections/Components.jsx — §3.4 Platform Components.
 *
 * Apple-style module showcase.  13 modules, each with a large mini-
 * preview (reused from the Phase 2.2 architecture engine so we get
 * consistent visuals for free), business impact, capabilities, an
 * example workflow and connected modules.
 *
 * Layout alternates left/right so the scroll feels cinematic.
 */

import React from 'react'
import clsx from 'clsx'
import Link from '@/shims/NextLink'
import { ArrowRight, Sparkles, Zap, Link2 } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { COMPONENTS } from '../data'
import { PREVIEWS } from '@/components/landing/home/architecture/previews'

const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function Components() {
  return (
    <section id="platform-components" className="relative py-24 md:py-32">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.10), transparent 70%)' }} />
      <Container size="wide" className="flex flex-col gap-16 md:gap-20">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Platform Components</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Thirteen modules. <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One story.</span></>}
            subtitle="Every module below ships pre-integrated with every other. No middleware, no data warehouse, no glue code — they simply speak the same language."
          />
        </div>

        <div className="flex flex-col gap-24 md:gap-28">
          {COMPONENTS.map((m, idx) => (
            <ComponentBlock key={m.id} module={m} flipped={idx % 2 === 1} />
          ))}
        </div>
      </Container>
    </section>
  )
}

function ComponentBlock({ module: m, flipped }) {
  const tone = TONE[m.tone] || TONE.orange
  const Preview = PREVIEWS[m.preview] || null
  return (
    <div id={`module-${m.id}`} className={clsx(
      'grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center scroll-mt-24',
    )}>
      {/* Preview side */}
      <div className={clsx('lg:col-span-6', flipped ? 'lg:order-2' : 'lg:order-1')}>
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: `radial-gradient(60% 40% at 20% 0%, rgba(${tone.rgb},0.14), transparent 70%)` }} />
          {/* window chrome */}
          <div className="relative flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5 mb-3">
            <div className="h-2 w-2 rounded-full bg-red-500/70" />
            <div className="h-2 w-2 rounded-full bg-amber-500/70" />
            <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
            <div className="ml-3 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
              app.kalkiai.trade/{m.id}
            </div>
          </div>
          <div className="relative min-h-[280px]">
            {Preview && <Preview />}
          </div>
        </div>
      </div>

      {/* Copy side */}
      <div className={clsx('lg:col-span-6 flex flex-col gap-4', flipped ? 'lg:order-1' : 'lg:order-2')}>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]"
            style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.hex }} />
            {m.eyebrow}
          </span>
        </div>
        <h3 className="text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">{m.name}</h3>
        <p className="text-base md:text-lg text-zinc-400 leading-relaxed">{m.purpose}</p>

        {/* Business value */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Business impact</div>
          <div className="text-sm text-white leading-relaxed">{m.impact}</div>
        </div>

        {/* Capabilities */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {m.capabilities.map((c, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-zinc-200">
              <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: tone.hex }} />
              <span>{c}</span>
            </div>
          ))}
        </div>

        {/* Workflow */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Example workflow</div>
          <ol className="flex flex-col md:flex-row md:items-center md:gap-1 gap-1.5 flex-wrap">
            {m.workflow.map((step, i) => (
              <React.Fragment key={i}>
                <li className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[11.5px] text-zinc-200">
                  <span className="font-mono text-[9.5px]" style={{ color: tone.hex }}>{i + 1}</span>
                  {step}
                </li>
                {i < m.workflow.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-zinc-500 hidden md:inline" />
                )}
              </React.Fragment>
            ))}
          </ol>
        </div>

        {/* Connected */}
        {m.connected?.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
              <Link2 className="h-3 w-3" /> Connected
            </span>
            {m.connected.map((c) => (
              <span key={c} className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.02] px-2 py-0.5 text-[10.5px] text-zinc-300">
                {c.replace(/-/g, ' ')}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="pt-1">
          <Link
            href={`/#module-${m.id}`}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:brightness-125"
            style={{ color: tone.hex }}
          >
            Learn more <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  )
}
